// Whether the signed-in user may buy a plan, answered from the server's own
// `access.can_purchase` (false for an active organisation member, whose plan
// comes from the organisation). Every purchase CTA and every automatic
// post-sign-in checkout asks this before offering or starting a checkout.
//
// Only an explicit `can_purchase === false` hides a purchase. A missing
// `access` (signed out, or a session stored before `access` existed) keeps
// today's behaviour and shows it; the server's 403
// `org_member_cannot_purchase` (checkoutRefusal.js) stays the backstop.
import { useEffect, useState } from 'react';

export const ORG_PLAN_NOTICE = 'Your plan comes from your organisation.';

const TOKEN_KEYS = [
  ['localStorage', 'pathbuddy_access_token'],
  ['sessionStorage', 'pathbuddy_access_token'],
  ['localStorage', 'token'],
];

const USER_KEYS = [
  ['localStorage', 'pathbuddy_user'],
  ['sessionStorage', 'pathbuddy_user'],
  ['localStorage', 'user'],
];

function readFirst(keys) {
  for (const [storeName, key] of keys) {
    try {
      const value = window[storeName].getItem(key);
      if (value) return value;
    } catch (err) {
      // Storage can be unavailable (private mode, blocked site data); treat
      // it as empty.
    }
  }
  return null;
}

/**
 * @param {object|null|undefined} user
 * @returns {boolean} false only when the server said this user cannot buy
 */
export function userMayPurchase(user) {
  return user?.access?.can_purchase !== false;
}

/**
 * The stored user of a signed-in session, read the way the landing pages
 * read it (token present, then pathbuddy_user / user). Null when signed out
 * or when the stored value is not a JSON object.
 *
 * @returns {object|null}
 */
export function readSignedInStoredUser() {
  if (typeof window === 'undefined') return null;
  if (!readFirst(TOKEN_KEYS)) return null;
  const raw = readFirst(USER_KEYS);
  if (typeof raw !== 'string' || !raw.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    return null;
  }
}

export function storedUserMayPurchase() {
  return userMayPurchase(readSignedInStoredUser());
}

/**
 * For pages that read the user from storage rather than AuthContext (the exam
 * landing pages). Re-reads when the session changes in this tab (authChange)
 * or another one (storage).
 *
 * @returns {boolean}
 */
export function useStoredUserMayPurchase() {
  const [mayPurchase, setMayPurchase] = useState(storedUserMayPurchase);

  useEffect(() => {
    const sync = () => setMayPurchase(storedUserMayPurchase());
    sync();
    window.addEventListener('authChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('authChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return mayPurchase;
}
